import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  fetchWithTimeout,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

const http3Endpoint = 'https://http3.drizt-freak.duckdns.org/';
const maxAttempts = 5;

function isHttp3(protocol: string): boolean {
  const normalized = protocol.toLowerCase();
  return normalized == 'h3' || normalized.startsWith('h3-');
}

export function normalizeHttpProtocol(protocol: string): string {
  let normalized = protocol.trim().toLowerCase();
  switch (normalized) {
    case 'http/3':
    case 'http/3.0':
      normalized = 'h3';
      break;
    case 'http/2':
    case 'http/2.0':
      normalized = 'h2';
      break;
  }
  return normalized;
}

async function requestProtocol(attempt: number): Promise<string> {
  const url = new URL(http3Endpoint);
  url.searchParams.set('http3Probe', String(attempt));
  const response = await fetchWithTimeout(
    url,
    { cache: 'no-store' },
    5000,
    'HTTP/3 endpoint request timed out after 5 seconds',
  );
  if (!response.ok) {
    throw new Error(`HTTP/3 endpoint returned ${response.status}`);
  }
  const body: unknown = await response.json();
  if (!isRecord(body) || typeof body.protocol != 'string') {
    throw new Error('HTTP/3 endpoint returned an invalid response');
  }
  return body.protocol;
}

async function collect(): Promise<RawData> {
  if (!window.isSecureContext) {
    throw new Error('HTTP/3 test requires a secure context');
  }

  let protocol = '';
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    protocol = await requestProtocol(attempt);
    if (isHttp3(normalizeHttpProtocol(protocol))) {
      break;
    }
  }

  return { protocol };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return isHttp3(normalizeHttpProtocol(String(rawData.protocol ?? '')))
    ? 'HTTP/3 works'
    : 'HTTP/3 failed';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function webRtcRemoteConnectionFailed(context: ValidationContext): boolean {
  const webRtc = context.results.webrtcRemoteFunctionality?.rawData;
  if (!webRtc) {
    return false;
  }
  return (
    !isRecord(webRtc.dataChannel) ||
    webRtc.dataChannel.received != 'abc-test-ping'
  );
}

function expectedConnectionFailure(): ValidationResult {
  return {
    valid: true,
    reason: null,
    note: 'HTTP/3 support failure is expected because the WebRTC remote connection also failed',
  };
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return webRtcRemoteConnectionFailed(context)
      ? expectedConnectionFailure()
      : error;
  }
  const reasons: string[] = [];
  if (typeof rawData.protocol != 'string') {
    reasons.push('HTTP/3 result is missing or invalid');
  } else if (!isHttp3(normalizeHttpProtocol(rawData.protocol))) {
    if (webRtcRemoteConnectionFailed(context)) {
      return expectedConnectionFailure();
    }
    reasons.push('HTTP/3 did not work. The proxy or route may block UDP/QUIC');
  }
  return validation(reasons);
}

export const http3SupportTest: TestDefinition = {
  id: 'http3Support',
  title: 'HTTP/3 support',
  category: 'stableProfile',
  format,
  run,
  validate,
};
